/**
 * How an audit row is put into words. Separate from the table component so the
 * founder dashboard can format a timestamp without importing a table.
 */
import type { TranslationKey } from '@/i18n'

const swissDateTime = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

export function formatMoment(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : swissDateTime.format(date)
}

/**
 * `dossier.uploaded` → "Dossier hochgeladen".
 *
 * An action with no translation falls back to the raw string rather than to a
 * blank cell: a new server-side action must be visible in the trail on the day
 * it starts being written, not on the day someone adds a label for it.
 */
export function actionLabel(action: string, t: (key: TranslationKey) => string): string {
  const key = `audit.action.${action}` as TranslationKey
  const label = t(key)
  return label === key ? action : label
}

/** True when the payload was stripped by a retention deletion. */
export function isRedacted(detail: Record<string, unknown> | null | undefined): boolean {
  return Boolean(detail && 'redacted' in detail)
}

/** Ids and counters as `key: value` pairs. The server decides what is in there. */
export function describeDetail(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .map(([key, value]) =>
      typeof value === 'object' && value !== null
        ? `${key}: ${JSON.stringify(value)}`
        : `${key}: ${String(value)}`,
    )
    .join(' · ')
}
